/**
 * NoPrDecisionCard - the agent's decision to finish a task without opening a
 * pull request, plus the human review of that decision
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Check, GitPullRequestClosed, Loader2, X } from 'lucide-react';
import { NoPrCategories, NoPrCategoryLabels, NoPrReviewStatusLabels } from '@resolve-ai/shared';
import type { NoPrCategory, NoPrReviewStatus } from '@resolve-ai/shared';
import { Card, StatusBadge } from '../common';
import { tasksApi } from '../../lib/tasks-api';
import type { NoPrReviewResult, NoPrVerdict } from '../../lib/tasks-api';

interface NoPrDecisionCardProps {
  taskId: string;
  category: NoPrCategory | null;
  reason: string | null;
  reviewStatus: NoPrReviewStatus;
  reviewedAt: string | null;
  onReviewed?: (result: NoPrReviewResult) => void;
}

interface ReviewState {
  status: NoPrReviewStatus;
  reviewedAt: string | null;
}

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed';

const BUTTON_IDLE =
  'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700';

const BUTTON_ACTIVE: Record<NoPrVerdict, string> = {
  approved: 'border-green-600 bg-green-600 text-white dark:border-green-500 dark:bg-green-600',
  rejected: 'border-red-600 bg-red-600 text-white dark:border-red-500 dark:bg-red-600',
};

function formatReviewDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function headingFor(category: NoPrCategory | null): string {
  if (category === NoPrCategories.NEEDS_PRODUCT_DECISION) return 'Waiting on a product decision';
  if (category === NoPrCategories.NEEDS_MANUAL_REPRODUCTION) return 'Waiting on a manual check';
  return 'No pull request needed';
}

export function NoPrDecisionCard({
  taskId,
  category,
  reason,
  reviewStatus,
  reviewedAt,
  onReviewed,
}: NoPrDecisionCardProps) {
  const [review, setReview] = useState<ReviewState>({ status: reviewStatus, reviewedAt });
  const [submitting, setSubmitting] = useState<NoPrVerdict | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isSubmitting = submitting !== null;
  const heading = headingFor(category);
  const hasVerdict = review.status !== 'pending';

  const handleVerdict = async (verdict: NoPrVerdict) => {
    if (review.status === verdict) return;

    setError(null);
    setSubmitting(verdict);
    try {
      const { task } = await tasksApi.reviewNoPrDecision(taskId, verdict);
      setReview({ status: task.noPrReviewStatus, reviewedAt: task.noPrReviewedAt });
      onReviewed?.(task);
    } catch (err) {
      console.error('Failed to review no-PR decision:', err);
      setError('Failed to save review. Try again.');
    } finally {
      setSubmitting(null);
    }
  };

  const renderVerdictButton = (verdict: NoPrVerdict, label: string, icon: ReactNode) => {
    const isActive = review.status === verdict;
    const isBusy = submitting === verdict;
    return (
      <button
        type="button"
        onClick={() => handleVerdict(verdict)}
        disabled={isSubmitting}
        aria-pressed={isActive}
        aria-busy={isBusy}
        className={`${BUTTON_BASE} ${isActive ? BUTTON_ACTIVE[verdict] : BUTTON_IDLE}`}
      >
        {isBusy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : icon}
        {label}
      </button>
    );
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <GitPullRequestClosed className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{heading}</h3>
            {category && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {NoPrCategoryLabels[category]}
              </p>
            )}
          </div>
        </div>
        <StatusBadge
          status={review.status}
          label={NoPrReviewStatusLabels[review.status]}
          size="sm"
        />
      </div>

      {reason ? (
        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line break-words p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          {reason}
        </p>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">No reason recorded</p>
      )}

      <div className="flex items-center flex-wrap justify-between gap-3 mt-4 pt-4 border-t border-gray-200/60 dark:border-gray-700/50">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {hasVerdict && review.reviewedAt
            ? `Reviewed ${formatReviewDate(review.reviewedAt)}`
            : 'Does this decision look right?'}
        </p>
        <div className="flex items-center gap-2">
          {renderVerdictButton('approved', 'Approve decision', <Check className="w-4 h-4" />)}
          {renderVerdictButton('rejected', 'Reject decision', <X className="w-4 h-4" />)}
        </div>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400 mt-3">
          {error}
        </p>
      )}
    </Card>
  );
}
